import { useEffect, useId, useRef, useState } from 'react';

/**
 * Labelled suggestions that follow choosing a template or an official occupation as the role title: "Suggested from the Payroll Specialist template:
 * Job family: <the suggested family> [Apply]". Each one is applied only by its own explicit click. If the field already holds a different value the click
 * first asks "Replace what you wrote?" with Keep mine as the default, so nothing the person entered is overwritten without a second, deliberate click.
 */

export interface ApplyChip {
  id: string;
  /** "Job family" */
  label: string;
  /** "the suggested value" */
  display: string;
  /** What the field holds now ('' when empty). A chip whose value is already in the field is not shown. */
  current: string;
  /** For example "Create department…" instead of "Apply" when applying opens another dialog. */
  actionLabel?: string;
  /** Skips the replace confirmation (the action does not write the field itself). */
  noConfirm?: boolean;
  onApply: () => void | Promise<void>;
}

export interface ApplyChipsProps {
  /** "the Payroll Specialist template" */
  source: string;
  chips: ApplyChip[];
  onDismiss: () => void;
}

function Chip({ chip }: { chip: ApplyChip }) {
  const uid = useId();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const applyRef = useRef<HTMLButtonElement>(null);
  const differs = chip.current.trim() !== '' && chip.current.trim().toLowerCase() !== chip.display.trim().toLowerCase();

  useEffect(() => {
    if (confirming) keepRef.current?.focus();
  }, [confirming]);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await chip.onApply();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be applied.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="jr-chipsuggest">
      <span id={`${uid}-text`}><strong>{chip.label}:</strong> {chip.display}</span>
      {differs && !confirming && <span className="jr-hint"> (you have {chip.current} now)</span>}
      {!confirming && (
        <button
          ref={applyRef} type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={busy}
          onClick={() => { if (differs && !chip.noConfirm) setConfirming(true); else void run(); }}
        >
          {chip.actionLabel ?? 'Apply'}{' '}<span className="jr-sr-only">{chip.label}: {chip.display}</span>
        </button>
      )}
      {confirming && (
        <span
          className="jr-inline-confirm jr-inline-confirm--row" role="group" aria-label={`Replace what you wrote in ${chip.label}?`}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); setConfirming(false); window.setTimeout(() => applyRef.current?.focus(), 0); } }}
        >
          <span><strong>Replace what you wrote?</strong> {chip.label} is {chip.current} now.</span>
          <button ref={keepRef} type="button" className="jr-btn jr-btn--primary jr-btn--small" onClick={() => { setConfirming(false); window.setTimeout(() => applyRef.current?.focus(), 0); }}>
            Keep mine
          </button>
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={busy} onClick={() => { setConfirming(false); void run(); }}>Replace</button>
        </span>
      )}
      {error && <span className="jr-error" role="alert"> {error}</span>}
    </li>
  );
}

export function ApplyChips({ source, chips, onDismiss }: ApplyChipsProps) {
  const shown = chips.filter((c) => c.current.trim().toLowerCase() !== c.display.trim().toLowerCase());
  if (shown.length === 0) return null;
  return (
    <div className="jr-suggestion jr-chips-box" role="group" aria-label={`Suggestions from ${source}`} data-testid="suggestion-chips">
      <p className="jr-suggestion__title">Suggested from {source}</p>
      <p className="jr-hint">Nothing is changed until you choose Apply for it. Your own entries are kept unless you say otherwise.</p>
      <ul className="jr-plain-list jr-chipsuggest-list">
        {shown.map((c) => <Chip key={c.id} chip={c} />)}
      </ul>
      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={onDismiss}>Dismiss suggestions</button>
    </div>
  );
}
